(() => {
  'use strict';
  const root = document.getElementById('bld-adrian-ai');
  if (!root || root.dataset.initialized) return;
  root.dataset.initialized = 'true';
  const launcher = root.querySelector('.adrian-launcher');
  const panel = root.querySelector('.adrian-panel');
  const close = root.querySelector('.adrian-close');
  const form = root.querySelector('.adrian-form');
  const input = root.querySelector('textarea');
  const send = form.querySelector('button');
  const log = root.querySelector('.adrian-messages');
  const status = root.querySelector('.adrian-status');
  const dismiss = document.createElement('button');
  dismiss.type = 'button';
  dismiss.className = 'adrian-dismiss';
  dismiss.textContent = '×';
  dismiss.setAttribute('aria-label', 'Hide Ask Adrian for this visit');
  dismiss.title = 'Hide Ask Adrian for this visit';
  root.append(dismiss);
  const dismissalKey = 'bld-adrian-dismissed';
  function rememberDismissal(hidden) {
    try {
      if (hidden) sessionStorage.setItem(dismissalKey, 'true');
      else sessionStorage.removeItem(dismissalKey);
    } catch (_) { /* Dismissal still works on this page without storage. */ }
  }
  dismiss.addEventListener('click', () => {
    panel.hidden = true;
    launcher.setAttribute('aria-expanded', 'false');
    root.hidden = true;
    rememberDismissal(true);
    dismiss.blur();
  });
  let endpoint = null;
  let clientId = null;
  let busy = false;
  function getClientId() {
    if (clientId) return clientId;
    try {
      const saved = sessionStorage.getItem('bld-adrian-client-id');
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(saved || '')) clientId = saved;
    } catch (_) { /* Session storage can be unavailable in private browsing. */ }
    if (!clientId) {
      clientId = crypto.randomUUID();
      try { sessionStorage.setItem('bld-adrian-client-id', clientId); } catch (_) { /* Use memory only. */ }
    }
    return clientId;
  }
  try {
    if (root.dataset.endpoint.trim()) {
      const candidate = new URL(root.dataset.endpoint, location.origin);
      if (candidate.protocol === 'https:' && !candidate.username && !candidate.password) endpoint = candidate.href;
    }
  } catch (_) { /* An invalid endpoint leaves the coming-soon panel active. */ }
  function setOpen(open) {
    if (open) {
      root.hidden = false;
      rememberDismissal(false);
    }
    dismiss.hidden = open;
    panel.hidden = !open;
    launcher.setAttribute('aria-expanded', String(open));
    if (open) (endpoint ? input : close).focus();
    else launcher.focus();
  }
  function visitAdrian(trigger) {
    const current = new URL(location.href);
    let destination = new URL('/pages/ask-adrian', current);
    const href = trigger?.getAttribute('href');
    if (href) {
      try {
        const linked = new URL(href, current);
        if (linked.origin === current.origin && /\/pages\/ask-adrian\/?$/.test(linked.pathname)) destination = linked;
      } catch (_) { /* Use the site's Ask Adrian page for buttons without a valid page link. */ }
    }
    if (current.pathname.replace(/\/$/, '') === destination.pathname.replace(/\/$/, '')) {
      setOpen(true);
      return;
    }
    // Keep the selected draft theme when navigating in a standalone preview.
    for (const key of ['preview_theme_id', '_fd', 'pb']) {
      if (current.searchParams.has(key) && !destination.searchParams.has(key)) destination.searchParams.set(key, current.searchParams.get(key));
    }
    destination.searchParams.set('adrian_chat', 'open');
    location.assign(destination.href);
  }
  launcher.addEventListener('click', () => panel.hidden ? visitAdrian(launcher) : setOpen(false));
  close.addEventListener('click', () => setOpen(false));
  function openFromPage(event) {
    const trigger = event.target?.closest?.('a, button, [role="button"]');
    if (!trigger || root.contains(trigger) || event.button > 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const labels = [trigger.getAttribute('aria-label'), trigger.innerText, trigger.textContent];
    if (!trigger.hasAttribute('data-adrian-open') && !labels.some(label => /^ask\s+adrian\s*[→↗›»➜]*$/i.test((label || '').trim()))) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    visitAdrian(trigger);
  }
  // Capture before theme navigation handlers; include accessible embedded sections.
  const connectedDocuments = new WeakSet();
  const connectedFrames = new WeakSet();
  function connectPage(page) {
    if (!page || connectedDocuments.has(page)) return;
    connectedDocuments.add(page);
    page.addEventListener('click', openFromPage, true);
    const scan = () => page.querySelectorAll('iframe').forEach(frame => {
      if (connectedFrames.has(frame)) return;
      connectedFrames.add(frame);
      const connectFrame = () => {
        try { connectPage(frame.contentDocument); } catch (_) { /* External frames own their navigation. */ }
      };
      frame.addEventListener('load', connectFrame);
      connectFrame();
    });
    scan();
    new MutationObserver(scan).observe(page.documentElement, { childList: true, subtree: true });
  }
  connectPage(document);
  root.addEventListener('keydown', event => {
    if (event.key === 'Escape' && !panel.hidden) {
      event.stopPropagation();
      setOpen(false);
    }
  });
  function addMessage(label, text, user, sources = []) {
    const item = document.createElement('div');
    item.className = 'adrian-message' + (user ? ' adrian-message-user' : '');
    const name = document.createElement('strong');
    name.textContent = label;
    item.append(name, document.createTextNode(text));
    sources.slice(0, 5).forEach(source => {
      if (!source || typeof source.url !== 'string') return;
      try {
        const url = new URL(source.url);
        if (url.protocol !== 'https:' || url.username || url.password) return;
        const link = document.createElement('a');
        link.href = url.href;
        link.textContent = typeof source.title === 'string' ? source.title : url.hostname;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        item.append(link);
      } catch (_) { /* Ignore malformed source links. */ }
    });
    log.append(item);
    while (log.children.length > 40) log.firstElementChild.remove();
    const body = root.querySelector('.adrian-body');
    body.scrollTop = body.scrollHeight;
  }
  form.addEventListener('submit', async event => {
    event.preventDefault();
    const message = input.value.trim();
    if (!endpoint || busy) return;
    if (message.length < 4 || message.length > 300) {
      status.textContent = 'Please use between 4 and 300 characters.';
      return;
    }
    busy = true;
    send.disabled = true;
    input.readOnly = true;
    addMessage('You', message, true);
    status.textContent = 'Adrian AI is thinking...';
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 30000);
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'omit',
        signal: controller.signal,
        body: JSON.stringify({ message, clientId: getClientId(), pageUrl: root.dataset.pageUrl })
      });
      const data = await response.json();
      if (!response.ok) {
        if (response.status === 429 || response.status === 400) {
          status.textContent = typeof data.error === 'string' ? data.error : 'Please try again later.';
          return;
        }
        throw new Error('Chat unavailable');
      }
      if (typeof data.answer !== 'string' || !data.answer.trim()) throw new Error('Missing answer');
      addMessage('Adrian AI', data.answer, false, Array.isArray(data.sources) ? data.sources : []);
      input.value = '';
      status.textContent = '';
    } catch (_) {
      status.textContent = 'Sorry, Adrian AI could not reply. Your question is still below so you can try again.';
    } finally {
      clearTimeout(timeout);
      busy = false;
      send.disabled = false;
      input.readOnly = false;
    }
  });
  if (endpoint) {
    root.querySelector('.adrian-notice').hidden = true;
    form.hidden = false;
  }
  root.hidden = false;
  try { root.hidden = sessionStorage.getItem(dismissalKey) === 'true'; } catch (_) { /* Show by default. */ }
  const arrival = new URL(location.href);
  if (arrival.searchParams.get('adrian_chat') === 'open') {
    setOpen(true);
    arrival.searchParams.delete('adrian_chat');
    try { history.replaceState(history.state, '', arrival.href); } catch (_) { /* Opening still works if history is unavailable. */ }
  }
})();
